import { apiQueryAdapter } from "../apiQueryAdapter";
import { PageResponseModel } from "../common/models";
import {
  ZipRoutingProfile,
  ZipRoutingProfileActivityLogFilters,
  ZipRoutingProfileActivityLogModel,
  ZipRoutingProfileFormModel,
  ZipRoutingProfileListParams,
} from "./models";

const DEFAULT_PAGE_SIZE = 50;

class ZipRoutingProfileService {
  private static instance: ZipRoutingProfileService;

  private constructor() {}

  static getInstance(): ZipRoutingProfileService {
    if (!ZipRoutingProfileService.instance) {
      ZipRoutingProfileService.instance = new ZipRoutingProfileService();
    }
    return ZipRoutingProfileService.instance;
  }

  public getList = async (
    filters: ZipRoutingProfileListParams = {}
  ): Promise<PageResponseModel<ZipRoutingProfile>> => {
    const params = new URLSearchParams();

    if (filters.search?.trim()) params.set("search", filters.search.trim());
    if (filters.state?.trim()) params.set("state", filters.state.trim());
    if (filters.terminalNumber?.trim()) {
      params.set("terminalNumber", filters.terminalNumber.trim());
    }
    if (filters.direct === "true" || filters.direct === "false") {
      params.set("direct", filters.direct);
    }
    if (filters.page !== undefined) params.set("page", String(filters.page));
    params.set("size", String(filters.size ?? DEFAULT_PAGE_SIZE));

    const response = await apiQueryAdapter.query("/zip-routing-profiles", "GET", {
      params,
    });
    return response.data;
  };

  public getById = async (id: string): Promise<ZipRoutingProfile> => {
    if (!id) throw new Error("Profile ID is required");
    const response = await apiQueryAdapter.query(`/zip-routing-profiles/${id}`, "GET");
    return response.data;
  };

  public create = async (
    data: ZipRoutingProfileFormModel
  ): Promise<ZipRoutingProfile> => {
    if (!data) throw new Error("Data is required");
    const response = await apiQueryAdapter.query<ZipRoutingProfile>(
      "/zip-routing-profiles",
      "POST",
      { body: this.toPayload(data) }
    );
    return response.data;
  };

  public update = async (
    id: string,
    data: ZipRoutingProfileFormModel
  ): Promise<ZipRoutingProfile> => {
    if (!id) throw new Error("Profile ID is required");
    if (!data) throw new Error("Data is required");
    const response = await apiQueryAdapter.query<ZipRoutingProfile>(
      `/zip-routing-profiles/${id}`,
      "PUT",
      { body: this.toPayload(data) }
    );
    return response.data;
  };

  public getActivityLogList = async (
    page?: number,
    filters: ZipRoutingProfileActivityLogFilters = {}
  ): Promise<PageResponseModel<ZipRoutingProfileActivityLogModel>> => {
    const params = new URLSearchParams();

    if (page !== undefined) params.set("page", String(page));
    if (filters.search?.trim()) params.set("search", filters.search.trim());
    if (filters.action) params.set("action", filters.action);
    if (filters.dateFrom) params.set("dateFrom", filters.dateFrom);
    if (filters.dateTo) params.set("dateTo", filters.dateTo);

    const response = await apiQueryAdapter.query(
      "/zip-routing-profiles/activity-log/all",
      "GET",
      { params }
    );
    return response.data;
  };

  /** Maps UI form → ZipRoutingProfileRequest body (no city) */
  private toPayload = (data: ZipRoutingProfileFormModel) => {
    const notServiced = data.coverageStatus === "NOT_SERVICED";
    return {
      zip: data.zipCode?.trim(),
      state: data.state?.trim(),
      terminalNumber: notServiced ? null : data.terminalNumber,
      coverageStatus: data.coverageStatus,
      rateType: data.direct ? "D" : "I",
      serviceMon: !!data.monday,
      serviceTue: !!data.tuesday,
      serviceWed: !!data.wednesday,
      serviceThu: !!data.thursday,
      serviceFri: !!data.friday,
      serviceSat: !!data.saturday,
      serviceSun: !!data.sunday,
      quoteAllowed: !!data.quoteAllowed,
      threePlOriginCode: data.threePlOriginCode?.trim() || null,
      threePlDestCode: data.threePlDestCode?.trim() || null,
      extensionCode1: data.extensionCode1?.trim() || null,
      extensionCode2: data.extensionCode2?.trim() || null,
    };
  };
}

export default ZipRoutingProfileService;
